import {
  AppBar,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import { styles } from "./Header.styles"
import UserAvatar from "components/User/UserAvatar"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import { maxWidth900 } from "themes/default/breakpoints"
import React, { useState } from "react"
import { FaSlidersH, FaRegLifeRing, FaMusic } from "react-icons/fa"
import LogoutIcon from "@mui/icons-material/Logout"
import Brightness4Icon from "@mui/icons-material/Brightness4"
import { useThemeContext } from "contexts/ThemeContext"
import useSystemDarkMode from "hooks/useSystemDarkMode"

export const HeaderUI = ({
  currentRoute = "/",
  userData,
  onOpenDrawer = () => {},
  onNavLinkClick = () => {},
  pagesLeft = [],
  pagesRight = [],
  userLeftContent = null,
  userRightContent = null,
  centerContent = null,
  onTutorialsChanged = () => {},
  onAudioEffectsChanged = () => {},
  onDarkModeChanged = () => {},
  onLogout = () => {},
  onSettingsClick = () => {},
}) => {
  const { t } = useTranslation()
  const isSystemDarkMode = useSystemDarkMode()
  const theme = useTheme()
  const matches = useMediaQuery(maxWidth900)
  const matchesLarges = useMediaQuery(
    "@media (max-height: 450px) and (min-width: 600px)"
  )
  const isActive = (path) => currentRoute.startsWith(path)
  const [anchorEl, setAnchorEl] = useState(null)
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false)
  const { fontSize, bgColor } = useThemeContext()

  const openSettingsMenu = (e) => {
    setAnchorEl(e.currentTarget)
    setSettingsMenuOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSettingsMenuOpen(false)
  }

  return (
    <React.Fragment>
      <AppBar
        position="fixed"
        className={`app-header`}
        sx={styles.header}
        elevation={0}
      >
        <Toolbar>
          <Stack
            direction={"row"}
            alignItems={"center"}
            flex={1}
            sx={styles.headerLeft}
          >
            <IconButton
              size="large"
              edge="start"
              aria-label="menu"
              sx={styles.menuButton}
              onClick={onOpenDrawer}
            >
              <MenuIcon
                color={theme.palette.mode === "dark" ? "#E6E6E6" : "primary"}
              />
            </IconButton>
            {userLeftContent}
          </Stack>
          <Stack
            flexGrow={1}
            justifyContent={"center"}
            alignItems={"center"}
            direction={"row"}
            sx={styles.headerCenterContent}
          >
            {!matches && !matchesLarges && (
              <Stack
                pr={0.5}
                direction={"row"}
                flex={1}
                justifyContent={"flex-end"}
                sx={styles.headerCenter}
              >
                {pagesLeft.map((page) => (
                  <NavLink
                    to={page.path}
                    key={page.label}
                    onClick={(e) => onNavLinkClick(e, page)}
                  >
                    <MenuItem
                      sx={[
                        styles.headerMenuItem,
                        isActive(page.path) && styles.activeMenuItem,
                      ]}
                    >
                      <Typography
                        textAlign="center"
                        sx={styles.headerMenuItemText}
                      >
                        {t(page.label)}
                      </Typography>
                    </MenuItem>
                  </NavLink>
                ))}
              </Stack>
            )}
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              sx={styles.headerMenuItemText}
            >
              {centerContent}
            </Stack>
            {!matches && !matchesLarges && (
              <Stack
                pl={0.5}
                direction={"row"}
                flex={1}
                justifyContent={"flex-start"}
                sx={styles.headerCenter}
              >
                {pagesRight.map((page) => (
                  <NavLink
                    to={page.path}
                    key={page.label}
                    onClick={(e) => onNavLinkClick(e, page)}
                  >
                    <MenuItem
                      sx={[
                        styles.headerMenuItem,
                        isActive(page.path) && styles.activeMenuItem,
                      ]}
                    >
                      <Typography
                        textAlign="center"
                        sx={styles.headerMenuItemText}
                      >
                        {t(page.label)}
                      </Typography>
                    </MenuItem>
                  </NavLink>
                ))}
              </Stack>
            )}
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            flex={1}
            justifyContent={"flex-end"}
          >
            {userRightContent}
            <IconButton
              onClick={openSettingsMenu}
              size="small"
              aria-controls={settingsMenuOpen ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={settingsMenuOpen ? "true" : undefined}
            >
              <UserAvatar sx={styles.userAvatar} />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={settingsMenuOpen}
        onClose={handleClose}
        slotProps={{
          root: {},
          backdrop: {
            sx: {
              backgroundColor: "transparent !important", // backdrop transparente
            },
          },
          paper: {
            // elevation: 0,
            sx: {
              minWidth: 250,
              // border: "1px solid #d4d4d5",
              border: "1px solid transparent",
              p: ".833em 1em",
              color: "rgba(0,0,0,.87)",
              boxShadow:
                "0 2px 4px 0 rgba(34,36,38,.12),0 2px 10px 0 rgba(34,36,38,.15)",
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: bgColor,
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Typography
          textAlign={"center"}
          color={
            theme.palette.mode === "light"
              ? theme.palette.text.lighter1
              : theme.palette.common.black
          }
          p=".5rem"
        >{`${userData?.name} ${userData?.lastName}`}</Typography>
        <Divider />
        <MenuItem
          onClick={() => {
            handleClose()
            onSettingsClick()
          }}
          disableGutters
          sx={[
            styles.menuItem,
            { fontSize: parseInt(fontSize?.replace("px", "")) - 4 },
          ]}
        >
          <ListItemIcon>
            <FaSlidersH fontSize="small" />
          </ListItemIcon>
          {t("Settings")}
        </MenuItem>
        <MenuItem
          sx={[
            styles.menuItem,
            { fontSize: parseInt(fontSize?.replace("px", "")) - 4 },
          ]}
          disableGutters
        >
          <ListItemIcon>
            <FaRegLifeRing fontSize="small" />
          </ListItemIcon>
          {t("Tutorials")}
          <Switch
            checked={userData?.allowTutorials}
            onChange={onTutorialsChanged}
            sx={styles.tutorialSwitch}
          />
        </MenuItem>
        <MenuItem
          disableGutters
          sx={[
            styles.menuItem,
            { fontSize: parseInt(fontSize?.replace("px", "")) - 4 },
          ]}
        >
          <ListItemIcon>
            <FaMusic fontSize="small" />
          </ListItemIcon>
          {t("audio_effects")}

          <Switch
            checked={userData?.allowSoundEffects}
            onChange={onAudioEffectsChanged}
            sx={styles.tutorialSwitch}
          />
        </MenuItem>
        <MenuItem
          disableGutters
          sx={[
            styles.menuItem,
            { fontSize: parseInt(fontSize?.replace("px", "")) - 4 },
          ]}
          disabled={userData?.uiSettings?.theme?.toLowerCase() === "auto"}
        >
          <ListItemIcon>
            <Brightness4Icon
              sx={{ fontSize: parseInt(fontSize?.replace("px", "")) - 4 }}
            />
          </ListItemIcon>
          {userData?.uiSettings?.theme?.toLowerCase() === "auto"
            ? t("auto_mode")
            : t("dark_mode")}
          <Switch
            checked={
              userData?.uiSettings?.theme?.toLowerCase() === "auto"
                ? isSystemDarkMode
                : userData?.uiSettings?.theme?.toLowerCase() === "dark"
            }
            onChange={onDarkModeChanged}
            sx={styles.tutorialSwitch}
            disabled={userData?.uiSettings?.theme?.toLowerCase() === "auto"}
          />
        </MenuItem>
        <MenuItem
          onClick={onLogout}
          disableGutters
          sx={[
            styles.menuItem,
            { fontSize: parseInt(fontSize?.replace("px", "")) - 4 },
          ]}
        >
          <ListItemIcon>
            <LogoutIcon
              sx={{ fontSize: parseInt(fontSize?.replace("px", "")) - 4 }}
            />
          </ListItemIcon>
          {t("Logout")}
        </MenuItem>
      </Menu>
    </React.Fragment>
  )
}
